import React, { useState } from 'react';

import {
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Icon,
    Input,
    InputGroup,
} from '@chakra-ui/react';
import { Field, FieldProps, useFormikContext } from 'formik';
import { HiOutlineUpload } from 'react-icons/hi';

import { CustomAvatar } from '@components/custom-avatar';
import { CustomText } from '@components/Custom-text';
import { useAppDispatch } from '@hooks/store';
import { showToast } from '@slices/apiSlice';
import { checkIsFileGTLimit } from '@utils/helpers';

interface FormControlImageFieldProps {
    imageId?: string | null;
    name: string;
    isDisabled?: boolean;
}

const FormControlImageField: React.FC<FormControlImageFieldProps> = ({
    imageId,
    name,
    isDisabled = false,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState(imageId);
    const maxFileSizeAllowed: number = 2;
    const { setFieldValue } = useFormikContext();
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        setFieldValue(name, file);
    }, [file, setFieldValue, name]);

    return imageUrl || file ? (
        <CustomAvatar
            file={file}
            setFile={setFile}
            setIsImage={setImageUrl}
            imageUrl={imageUrl ?? ''}
        />
    ) : (
        <Field name={name}>
            {({ field, form: { touched, errors } }: FieldProps) => {
                const isInvalid = !!errors[field.name] && touched[field.name];
                return (
                    <FormControl
                        isInvalid={Boolean(isInvalid)}
                        width="100%"
                        display="flex"
                        justifyContent="center"
                    >
                        <InputGroup width="max-content">
                            <Input
                                id={name}
                                name={name}
                                type="file"
                                isDisabled={isDisabled}
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={e => {
                                    const fileToUpload = e.target.files ? e.target.files[0] : null;
                                    const fileSize = fileToUpload?.size ?? 0;
                                    if (checkIsFileGTLimit(fileSize, maxFileSizeAllowed)) {
                                        dispatch(
                                            showToast({
                                                variant: 'error',
                                                description: `Profile Picture cannot be greater than ${maxFileSizeAllowed}MB`,
                                            })
                                        );
                                    } else {
                                        setFile(fileToUpload);
                                    }
                                    e.target.value = '';
                                }}
                            />
                            <FormLabel htmlFor={name} width="100%" margin={0}>
                                <Flex width="100%" alignItems="center" justifyContent="center">
                                    <Flex
                                        width={100}
                                        height={100}
                                        borderRadius={50}
                                        border={2}
                                        borderColor="mainText"
                                        borderStyle="solid"
                                        alignItems="center"
                                        justifyContent="center"
                                    >
                                        <Icon
                                            cursor="pointer"
                                            as={HiOutlineUpload}
                                            fontSize={32}
                                            color="mainText"
                                        />
                                    </Flex>
                                </Flex>
                            </FormLabel>
                        </InputGroup>
                        <FormErrorMessage>
                            <CustomText size="xs" color="red.500">
                                {String(errors[field.name])}
                            </CustomText>
                        </FormErrorMessage>
                    </FormControl>
                );
            }}
        </Field>
    );
};

export default FormControlImageField;
